// @flow strict

/**
 * (Copied to jsx from utils/MouseAway)
 * Make a polygon of coordinates out of two elements. This is optimized for overlays, so it makes some assumptions:
 * 1) The overlay is not completely offset from source. That is, they come close to sharing an edge.
 * 2) elm2 is larger in the shared-edge dimension than elm1 If this is not the case, the code won"t work quite as
 *    well. Mouse movement will only be allowed inside a concave intersection instead of convex.
 *
 * @param {Object} elm1 - First element
 * @param {Object} elm2 - Second element. This is assumed to be larger in the shared-edge dimension than elm1
 * @returns {Array|null} - The shared polygon, or null if the two rectangles do not nearly share an edge.
 * @private
 */
function makePoly(elm1: HTMLElement, elm2: HTMLElement) {
  const r1 = elm1.getBoundingClientRect();
  const r2 = elm2.getBoundingClientRect();
  const r1Top = r1.top;
  const r1Bottom = r1.bottom;
  const r1Left = r1.left;
  const r1Right = r1.right;
  const r2Top = r2.top;
  const r2Bottom = r2.bottom;
  const r2Left = r2.left;
  const r2Right = r2.right;

  if (r1Right <= r2Left) {
    //       ----------
    //     /           |
    // ----            |
    // |r1        r2   |
    // ----            |
    //     \           |
    //      -----------
    return [
      [r1Left, r1Top],
      [r2Left, r2Top],
      [r2Right, r2Top],
      [r2Right, r2Bottom],
      [r2Left, r2Bottom],
      [r1Left, r1Bottom],
    ];
  } else if (r1Left >= r2Right) {
    // -------
    // |      \
    // |       -----
    // | r2      r1 |
    // |       -----
    // |      /
    // -------
    return [
      [r1Right, r1Top],
      [r2Right, r2Top],
      [r2Left, r2Top],
      [r2Left, r2Bottom],
      [r2Right, r2Bottom],
      [r1Right, r1Bottom],
    ];
  } else if (r1Top >= r2Bottom) {
    // etc.
    return [
      [r1Right, r1Bottom],
      [r2Right, r2Bottom],
      [r2Right, r2Top],
      [r2Left, r2Top],
      [r2Left, r2Bottom],
      [r1Left, r1Bottom],
    ];
  } else if (r1Bottom <= r2Top) {
    return [
      [r1Right, r1Top],
      [r2Right, r2Top],
      [r2Right, r2Bottom],
      [r2Left, r2Bottom],
      [r2Left, r2Top],
      [r1Left, r1Top],
    ];
  }

  // We either don't need to do anything because the elements overlap, or
  // we can't do anything because of the angle between the elements.
  return null;
}

/**
 * Is the point inside the polygon?
 *
 * @param {Array} point - [x,y] coords of the point to test.
 * @param {Array} poly - Array of [x,y] vertices defining a polygon.
 * @returns {boolean} It is! It is inside the polygon!
 * @private
 */
function insidePoly(point: Array<number>, poly: ?Array<Array<number>>) {
  if (!poly) {
    return false;
  }

  // ray-casting algorithm based on
  // https://wrf.ecse.rpi.edu//Research/Short_Notes/pnpoly.html
  const x = point[0];
  const y = point[1];
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0];
    const yi = poly[i][1];
    const xj = poly[j][0];
    const yj = poly[j][1];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

export { makePoly, insidePoly };
